import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { toast } from 'sonner';

export type StripeMode = 'test' | 'production';

export interface SystemStripeModeInfo {
  stripe_mode: StripeMode;
  stripe_mode_changed_at: string | null;
  stripe_mode_changed_by: string | null;
  changed_by_name?: string | null;
}

export function useSystemStripeMode() {
  const { user, loading: authLoading } = useAuth();
  const queryClient = useQueryClient();

  const { data: stripeModeData, isLoading } = useQuery({
    queryKey: ['system-stripe-mode'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('system_stripe_config')
        .select('stripe_mode, stripe_mode_changed_at, stripe_mode_changed_by')
        .eq('is_active', true)
        .single();
      
      if (error) throw error;
      
      // Buscar o nome do usuário que fez a última alteração
      let changedByName = null;
      if (data.stripe_mode_changed_by) {
        const { data: profileData } = await supabase
          .from('profiles')
          .select('full_name')
          .eq('user_id', data.stripe_mode_changed_by)
          .single();
        
        changedByName = profileData?.full_name || null;
      }
      
      return {
        stripe_mode: data.stripe_mode,
        stripe_mode_changed_at: data.stripe_mode_changed_at,
        stripe_mode_changed_by: data.stripe_mode_changed_by,
        changed_by_name: changedByName,
      } as SystemStripeModeInfo;
    },
    enabled: true,
  });

  const updateMode = useMutation({
    mutationFn: async (newMode: StripeMode) => {
      if (authLoading) {
        throw new Error('Aguardando carregamento dos dados...');
      }
      
      if (!user) {
        throw new Error('Usuário não autenticado. Por favor, faça login novamente.');
      }
      
      const { error } = await supabase
        .from('system_stripe_config')
        .update({
          stripe_mode: newMode,
          stripe_mode_changed_at: new Date().toISOString(),
          stripe_mode_changed_by: user.id,
        })
        .eq('is_active', true);
      
      if (error) throw error;
    },
    onSuccess: (_data, newMode) => {
      queryClient.invalidateQueries({ queryKey: ['system-stripe-mode'] });
      
      const modeLabel = newMode === 'production' ? 'Produção' : 'Teste';
      toast.success(`Modo Stripe global alterado para ${modeLabel}`, {
        description: 'As configurações foram atualizadas com sucesso',
      });
    },
    onError: (error) => {
      toast.error('Erro ao alterar modo Stripe: ' + error.message);
    },
  });

  return {
    stripeMode: stripeModeData?.stripe_mode || 'test',
    isTestMode: stripeModeData?.stripe_mode === 'test',
    isProductionMode: stripeModeData?.stripe_mode === 'production',
    stripeModeChangedAt: stripeModeData?.stripe_mode_changed_at,
    stripeModeChangedBy: stripeModeData?.stripe_mode_changed_by,
    changedByName: stripeModeData?.changed_by_name,
    updateMode: updateMode.mutateAsync,
    isUpdating: updateMode.isPending || authLoading,
    isLoading: isLoading || authLoading,
  };
}
